import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    invoice_number: '',
    client_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    vat_rate: 20.00,
    discount: 0,
    notes: '',
  });

  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, discount_percentage: 0, line_total: 0 }
  ]);

  useEffect(() => {
    fetchClients();
    if (isEditMode) {
      fetchInvoice();
    }
  }, [id]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/clients?is_active=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch clients');

      const data = await response.json();
      setClients(data.clients || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const fetchInvoice = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/invoices/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch invoice');

      const data = await response.json();
      setFormData({
        invoice_number: data.invoice.invoice_number,
        client_id: data.invoice.client_id,
        invoice_date: data.invoice.invoice_date.split('T')[0],
        due_date: data.invoice.due_date ? data.invoice.due_date.split('T')[0] : '',
        vat_rate: data.invoice.vat_rate,
        discount: data.invoice.discount,
        notes: data.invoice.notes || '',
        status: data.invoice.status,
        subtotal: data.invoice.subtotal,
        vat_amount: data.invoice.vat_amount,
        total: data.invoice.total,
      });

      if (data.line_items && data.line_items.length > 0) {
        setLineItems(data.line_items.map((item) => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percentage: item.discount_percentage,
          line_total: item.line_total,
        })));
      }
    } catch (err) {
      setError('Failed to load invoice');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateLineTotal = (quantity, unitPrice, discountPercentage) => {
    const qty = Number(quantity) || 0;
    const price = Number(unitPrice) || 0;
    const discount = Number(discountPercentage) || 0;
    return qty * price * (1 - discount / 100);
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + (Number(item.line_total) || 0), 0);
    const discountAmount = Number(formData.discount) || 0;
    const subtotalAfterDiscount = subtotal - discountAmount;
    const vatAmount = subtotalAfterDiscount * (Number(formData.vat_rate) / 100);
    const total = subtotalAfterDiscount + vatAmount;

    return {
      subtotal: subtotal.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      subtotalAfterDiscount: subtotalAfterDiscount.toFixed(2),
      vatAmount: vatAmount.toFixed(2),
      total: total.toFixed(2),
    };
  };

  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      { description: '', quantity: 1, unit_price: 0, discount_percentage: 0, line_total: 0 }
    ]);
  };

  const handleRemoveLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };

    // Recalculate line total
    if (field === 'quantity' || field === 'unit_price' || field === 'discount_percentage') {
      updatedItems[index].line_total = calculateLineTotal(
        updatedItems[index].quantity,
        updatedItems[index].unit_price,
        updatedItems[index].discount_percentage
      );
    }

    setLineItems(updatedItems);
  };

  const generateInvoiceNumber = () => {
    const year = new Date().getFullYear();
    const timestamp = Date.now().toString().slice(-6);
    return `INV-${year}-${timestamp}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Auto-generate invoice number if not provided (for new invoices only)
    const invoiceNumber = formData.invoice_number || (isEditMode ? formData.invoice_number : generateInvoiceNumber());

    // Validation
    if (!invoiceNumber || !formData.client_id || !formData.invoice_date) {
      setError('Please fill in all required fields');
      return;
    }

    if (lineItems.length === 0 || lineItems.every(item => !item.description)) {
      setError('Please add at least one line item');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const url = isEditMode ? `${API_URL}/invoices/${id}` : `${API_URL}/invoices`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          invoice_number: invoiceNumber,
          line_items: lineItems.filter(item => item.description.trim() !== ''),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save invoice');
      }

      alert(isEditMode ? 'Invoice updated successfully' : 'Invoice created successfully');
      navigate('/joblist/invoices');
    } catch (err) {
      setError(err.message || 'Failed to save invoice');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading invoice...</div>
      </div>
    );
  }

  const totals = calculateTotals();
  const isDraft = !isEditMode || formData.status === 'draft';

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'View Invoice' : 'Create Invoice'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? `Invoice #${formData.invoice_number}` : 'Create a new invoice'}
          </p>
        </div>
        <button
          onClick={() => navigate('/joblist/invoices')}
          className="px-4 py-2 text-gray-700 hover:text-gray-900"
        >
          ← Back to Invoices
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Invoice Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Number {!isEditMode && <span className="text-gray-500 text-xs">(auto-generated if blank)</span>}
              </label>
              <input
                type="text"
                disabled={isEditMode}
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                placeholder={isEditMode ? '' : 'Leave blank for auto-generation'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client *
              </label>
              <select
                required
                disabled={isEditMode}
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select a client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.company_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Date *
              </label>
              <input
                type="date"
                required
                disabled={!isDraft}
                value={formData.invoice_date}
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                disabled={!isDraft}
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                VAT Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                disabled={!isDraft}
                value={formData.vat_rate}
                onChange={(e) => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount (£)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                disabled={!isDraft}
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              disabled={!isDraft}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Line Items</h2>
            {!isEditMode && (
              <button
                type="button"
                onClick={handleAddLineItem}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                + Add Line Item
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount %</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line Total</th>
                  {!isEditMode && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lineItems.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        required
                        disabled={isEditMode}
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                        placeholder="Item description"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        disabled={isEditMode}
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        disabled={isEditMode}
                        value={item.unit_price}
                        onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="w-28 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        disabled={isEditMode}
                        value={item.discount_percentage}
                        onChange={(e) => handleLineItemChange(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium">
                      £{Number(item.line_total).toFixed(2)}
                    </td>
                    {!isEditMode && (
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(index)}
                          disabled={lineItems.length === 1}
                          className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Invoice Summary</h2>

          <div className="space-y-3 max-w-md ml-auto">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium">£{totals.subtotal}</span>
            </div>
            {formData.discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount:</span>
                <span className="font-medium text-red-600">-£{totals.discountAmount}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal after discount:</span>
              <span className="font-medium">£{totals.subtotalAfterDiscount}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">VAT ({formData.vat_rate}%):</span>
              <span className="font-medium">£{totals.vatAmount}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-3">
              <span>Total:</span>
              <span>£{totals.total}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        {!isEditMode && (
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/joblist/invoices')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Invoice
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

